import { create } from "zustand";
import { persist } from "zustand/middleware";
import { jwtDecode } from "jwt-decode";
import type { components } from "@/lib/api/schema";
import { useContextStore } from "@/store/contextStore";
import { queryClient } from "@/lib/api/queryClient";
import { revokeRefreshToken } from "@/lib/auth/revokeSession";
import { clearPendingMfaReturns } from "@/lib/auth/pendingRecoveryCodes";
import { recordActivity } from "@/lib/auth/activity";

type UserDetails = components["schemas"]["UserDetails"];
type PermissionGroupSummary = components["schemas"]["PermissionGroupSummary"];

export interface UserPermissionGroup {
  /** PermissionGroup UUID. Credential ids stay integers, as the API sends them. */
  id: string;
  name: string;
  permissions: Record<string, Record<string, boolean>>;
  allowed_credential_ids: number[];
  /** Organizations the group names, matched exactly (never their descendants). */
  allowed_organization_ids: string[];
  /** Every company under the member's own MSP. */
  can_view_all_companies: boolean;
  /**
   * The group's anchor. A group naming no accounts and no organizations
   * reaches this organization and everything beneath it; `null` is a system
   * group, which reaches everything.
   */
  organization_id: string | null;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId?: string;
  companyName?: string;
  mspId?: string;
  /** MSP name (from JWT / GET /auth/me/). */
  mspName?: string | null;
  distributorId?: string;
  distributorName?: string;
  mspStatus?: string | null;
  companyStatus?: string | null;
  /** True when role is `platform_manager` — a scoped platform_admin. */
  isAccountManager?: boolean;
  /** Distributor UUIDs this platform_manager may access. Empty for non-platform_managers. */
  allowedDistributorIds?: string[];
  /** MSP UUIDs this platform_manager may access. Empty for non-platform_managers. */
  allowedMspIds?: string[];
  /** Server-backed JSON preferences (e.g. layout); merged from GET /auth/me/. */
  preferences?: Record<string, unknown>;
  /**
   * Currencies this deployment can report cost in, from GET /auth/me/.
   *
   * Published by the server rather than assumed by the client: cost is
   * pre-converted into exactly this set at ingestion, so offering one the
   * backend does not know is a 400 and omitting one it supports hides a
   * working feature. Optional for a backend too old to send it.
   */
  reporting_currencies?: string[];
  /** From GET /auth/me/ `permission_groups` — drives `usePermissions` for non-admin roles. */
  permissionGroups?: UserPermissionGroup[];
  /** Whether the account email has been verified. */
  emailVerified?: boolean;
  /** False for SSO-provisioned users whose password was set to unusable. Used to hide password change form. */
  hasUsablePassword?: boolean;
  /** True when the user has a confirmed TOTPDevice (from GET /auth/me/). */
  mfaEnabled?: boolean;
  /** Profile fields from GET /auth/me/. They live on the account, not in `preferences`. */
  phone?: string;
  jobTitle?: string;
  /** URL of the uploaded profile photo; null when there is none. */
  avatar?: string | null;
}

interface TokenPayload {
  user_id: string;
  email: string;
  role: string;
  full_name: string;
  company_id?: string;
  company_name?: string;
  msp_id?: string;
  msp_name?: string | null;
  distributor_id?: string;
  distributor_name?: string;
  is_platform_manager?: boolean;
  allowed_distributor_ids?: string[];
  allowed_msp_ids?: string[];
  exp: number;
}

interface ImpersonationState {
  isImpersonating: boolean;
  originalUser?: User;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  impersonation: ImpersonationState;
  isAuthenticated: boolean;
  mspStatus: string | null;
  companyStatus: string | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  startImpersonation: (
    user: User,
    accessToken: string,
    refreshToken: string,
  ) => void;
  stopImpersonation: (accessToken: string, refreshToken: string) => void;
  isTokenExpired: () => boolean;
  setStatus: (mspStatus: string | null, companyStatus: string | null) => void;
  fetchMe: () => Promise<void>;
}

/** JWT claims use JSON null; keep store fields undefined so UI checks stay simple. */
function claimString(v: unknown): string | undefined {
  if (v == null || v === "") return undefined;
  const s = String(v);
  return s === "null" || s === "undefined" ? undefined : s;
}

function claimOptionalName(v: unknown): string | null | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return undefined;
  return s;
}

function parsePermissionGroups(
  raw: PermissionGroupSummary[] | undefined,
): UserPermissionGroup[] {
  if (!raw?.length) return [];
  return raw.map((g) => ({
    id: g.id,
    name: g.name,
    permissions:
      g.permissions != null &&
      typeof g.permissions === "object" &&
      !Array.isArray(g.permissions)
        ? (g.permissions as Record<string, Record<string, boolean>>)
        : {},
    allowed_credential_ids: g.allowed_credential_ids ?? [],
    allowed_organization_ids: (g.allowed_organization_ids ?? []).map(String),
    can_view_all_companies: g.can_view_all_companies === true,
    organization_id: g.organization_id ?? null,
  }));
}

const parseUserFromToken = (token: string): User | null => {
  try {
    const decoded = jwtDecode<TokenPayload>(token);
    const [firstName, ...lastNameParts] = decoded.full_name.split(" ");
    const user: User = {
      id: decoded.user_id,
      email: decoded.email,
      firstName: firstName || "",
      lastName: lastNameParts.join(" ") || "",
      role: decoded.role,
      companyId: claimString(decoded.company_id),
      companyName: claimOptionalName(decoded.company_name) ?? undefined,
      mspId: claimString(decoded.msp_id),
      mspName: claimOptionalName(decoded.msp_name) ?? null,
      distributorId: claimString(decoded.distributor_id),
      distributorName: claimOptionalName(decoded.distributor_name) ?? undefined,
      isAccountManager: decoded.is_platform_manager === true,
      allowedDistributorIds: Array.isArray(decoded.allowed_distributor_ids)
        ? decoded.allowed_distributor_ids.map(String)
        : [],
      allowedMspIds: Array.isArray(decoded.allowed_msp_ids)
        ? decoded.allowed_msp_ids.map(String)
        : [],
    };

    return user;
  } catch {
    return null;
  }
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The organization fields of a persisted user, re-read from its own token
 * when they cannot be real.
 *
 * Organization ids are UUIDs. A session saved by an earlier build that
 * replaced them with fixture ids for the demo logins would otherwise replay
 * those ids from storage on every load, and every page would ask the server
 * about organizations that do not exist. The token was always the real one,
 * so its claims are the repair. A user whose ids are already UUIDs is
 * returned as it is.
 */
function withTokenOrgIds(user: User, accessToken: string | null): User {
  const ids = [user.distributorId, user.mspId, user.companyId];
  if (ids.every((id) => id == null || UUID.test(id)) || !accessToken) {
    return user;
  }
  const claims = parseUserFromToken(accessToken);
  if (!claims) return user;
  return {
    ...user,
    firstName: claims.firstName,
    lastName: claims.lastName,
    distributorId: claims.distributorId,
    distributorName: claims.distributorName,
    mspId: claims.mspId,
    mspName: claims.mspName,
    companyId: claims.companyId,
    companyName: claims.companyName,
  };
}

/**
 * Shared promise so concurrent profile reads coalesce into one request.
 *
 * `fetchMe` has three callers that overlap by design: the poll, the focus and
 * visibility handlers in `useRoleSync`, and the 403 interceptor. A page that
 * mounts and fires six requests in parallel, all 403 -- exactly the
 * just-demoted reader this exists for -- would otherwise send six identical
 * `GET /auth/me/` and run six `set()` calls for one answer. `step_up_required`
 * is a 403 too, so every MFA step-up prompt paid the same cost for a response
 * that can never carry a role change.
 *
 * Mirrors `refreshPromise` in `lib/api/client.ts`. Cleared in `finally`, so a
 * failed read does not wedge the guard shut.
 */
let mePromise: Promise<void> | null = null;

/**
 * Drop every cached server answer. Called on each change of who the session
 * is: sign-in, sign-out, and both ends of an impersonation.
 *
 * The query cache holds one person's figures, organizations and channels, and
 * it outlives the change -- sign-in pages navigate without a reload, and
 * impersonation never reloads -- so the next person was served the previous
 * one's answers until each query happened to go stale.
 */
function forgetCachedAnswers(): void {
  queryClient.clear();
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      impersonation: {
        isImpersonating: false,
        originalUser: undefined,
      },
      isAuthenticated: false,
      mspStatus: null,
      companyStatus: null,

      setTokens: (accessToken: string, refreshToken: string) => {
        const user = parseUserFromToken(accessToken);
        // Every sign-in page lands here as well as a routine refresh. A
        // refresh is the same person and keeps the cache and the scope bar;
        // new tokens for anyone else, or a new role, are a change of identity.
        const previous = get().user;
        if (previous?.id !== user?.id || previous?.role !== user?.role) {
          // A sign-in is the reader at the console. Without this, the idle
          // clock a previous session left in storage would count against the
          // new one and could end it the moment the app opens. A role change
          // arriving on a background refresh is not anyone at the console.
          if (previous?.id !== user?.id) recordActivity();
          forgetCachedAnswers();
          // So a stale "all" scope from a previous session cannot bleed into
          // a lower-privileged account. Resetting on every refresh as well
          // threw the reader's selection away every few minutes.
          useContextStore.getState().clearContext();
        }
        set({
          accessToken,
          refreshToken,
          user,
          isAuthenticated: true,
        });
      },

      setUser: (user: User) => {
        set({ user });
      },

      login: (user: User, accessToken: string, refreshToken: string) => {
        // Whose recovery codes are owed is a fact about one identity. The flag
        // lives in sessionStorage, which outlives a sign-out and the navigation
        // after it, so it is cleared on every change of identity — not in
        // `setTokens`, which a routine token refresh also calls and which would
        // then discard the debt mid-enrollment.
        clearPendingMfaReturns();
        // A sign-in is activity; see `setTokens`.
        recordActivity();
        // Reset viewing scope whenever the logged-in identity changes so a
        // stale "all"/distributor context from a prior session cannot leak
        // into a lower-privileged account's first render (Phase U9).
        useContextStore.getState().clearContext();
        forgetCachedAnswers();
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },

      logout: () => {
        // Whose recovery codes are owed is a fact about one identity. The flag
        // lives in sessionStorage, which outlives a sign-out and the navigation
        // after it, so it is cleared on every change of identity — not in
        // `setTokens`, which a routine token refresh also calls and which would
        // then discard the debt mid-enrollment.
        clearPendingMfaReturns();
        const { refreshToken, accessToken } = get();
        // Revoke before the tokens are cleared, and pass them explicitly. The
        // previous version dynamically imported the axios client, whose request
        // interceptor reads the access token from this store — which `set`
        // below had already emptied, so the call went out unauthenticated,
        // answered 401, and revoked nothing. See `revokeRefreshToken`.
        if (refreshToken && accessToken) {
          revokeRefreshToken(accessToken, refreshToken);
        }
        useContextStore.getState().clearContext();
        forgetCachedAnswers();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          impersonation: {
            isImpersonating: false,
            originalUser: undefined,
          },
        });
      },

      startImpersonation: (
        user: User,
        accessToken: string,
        refreshToken: string,
      ) => {
        // Impersonation changes who the session is; see `logout`.
        clearPendingMfaReturns();
        const { user: currentUser } = get();
        const parsed = parseUserFromToken(accessToken);
        // The administrator's scope-bar selection is theirs. Kept, it narrowed
        // the impersonated person's view to it and the address repeated it.
        useContextStore.getState().clearContext();
        forgetCachedAnswers();
        set({
          accessToken,
          refreshToken,
          user: parsed ?? user,
          impersonation: {
            isImpersonating: true,
            originalUser: currentUser || undefined,
          },
        });
      },

      stopImpersonation: (accessToken: string, refreshToken: string) => {
        // Whose recovery codes are owed is a fact about one identity. The flag
        // lives in sessionStorage, which outlives a sign-out and the navigation
        // after it, so it is cleared on every change of identity — not in
        // `setTokens`, which a routine token refresh also calls and which would
        // then discard the debt mid-enrollment.
        clearPendingMfaReturns();
        // Halt + escalate if runtime rejects inferred shape (F33 carry-forward).
        const user = parseUserFromToken(accessToken);
        useContextStore.getState().clearContext();
        forgetCachedAnswers();
        set({
          accessToken,
          refreshToken,
          user,
          isAuthenticated: true,
          impersonation: {
            isImpersonating: false,
            originalUser: undefined,
          },
        });
      },

      isTokenExpired: () => {
        const { accessToken } = get();
        if (!accessToken) return true;

        // A token that does not decode is expired, so nobody can sign
        // themselves in by hand-crafting a localStorage blob.
        try {
          const decoded = jwtDecode<TokenPayload>(accessToken);
          return decoded.exp * 1000 < Date.now();
        } catch {
          return true;
        }
      },

      setStatus: (mspStatus, companyStatus) => {
        set({ mspStatus, companyStatus });
      },

      fetchMe: () => {
        if (mePromise) return mePromise;
        mePromise = (async () => {
          try {
            const { authApi } = await import("@/lib/api/auth");
            const me = await authApi.getMe();
            const details = me as UserDetails;
            set((state) => {
              const prefs =
                details.preferences != null &&
                typeof details.preferences === "object"
                  ? (details.preferences as Record<string, unknown>)
                  : state.user?.preferences;
              const companyIdStr =
                details.company != null
                  ? String(details.company)
                  : state.user?.companyId;
              const permissionGroups = parsePermissionGroups(
                details.permission_groups,
              );
              return {
                mspStatus: details.msp_status ?? null,
                companyStatus: details.company_status ?? null,
                user: state.user
                  ? {
                      ...state.user,
                      // The role the *server* holds, not the one minted into the
                      // access token at sign-in. An admin changing somebody's
                      // role takes effect immediately server-side, but the token
                      // in that person's browser keeps its old claim until it is
                      // rotated, so the sidebar kept offering pages the API had
                      // already started refusing. Every other field here was
                      // merged and this one was not, which is why calling
                      // `fetchMe` never fixed anything.
                      role: details.role ?? state.user.role,
                      isAccountManager:
                        details.role != null
                          ? details.role === "platform_manager"
                          : state.user.isAccountManager,
                      firstName: details.first_name ?? state.user.firstName,
                      lastName: details.last_name ?? state.user.lastName,
                      companyId: companyIdStr,
                      companyName:
                        details.company_name ?? state.user.companyName,
                      mspName: details.msp_name ?? state.user.mspName,
                      distributorId:
                        details.distributor_id ?? state.user.distributorId,
                      distributorName:
                        details.distributor_name ?? state.user.distributorName,
                      permissionGroups,
                      email: details.email ?? state.user.email,
                      phone: details.phone ?? state.user.phone,
                      jobTitle: details.job_title ?? state.user.jobTitle,
                      avatar:
                        details.avatar !== undefined
                          ? details.avatar
                          : state.user.avatar,
                      emailVerified:
                        details.email_verified ?? state.user.emailVerified,
                      hasUsablePassword:
                        details.has_usable_password ??
                        state.user.hasUsablePassword,
                      mfaEnabled:
                        (details as unknown as { mfa_enabled?: boolean })
                          .mfa_enabled ?? state.user?.mfaEnabled,
                      ...(prefs !== undefined ? { preferences: prefs } : {}),
                      // Without this the field is declared on `User` and read by
                      // `useReportingCurrency`, but never actually arrives, so the
                      // hook silently falls back to the local constant and the
                      // server-published list is authoritative in name only.
                      reporting_currencies:
                        (
                          details as unknown as {
                            reporting_currencies?: string[];
                          }
                        ).reporting_currencies ??
                        state.user?.reporting_currencies,
                    }
                  : state.user,
              };
            });
          } catch {
            // silently ignore — auth state still valid
          } finally {
            mePromise = null;
          }
        })();
        return mePromise;
      },
    }),
    {
      name: "everythingcloud-auth",
      // Bumped for the UUID primary-key migration (TW-45637889). A browser that
      // still holds the previous shape has a numeric `user.id` and a token whose
      // `user_id` claim is an integer — neither matches anything server-side any
      // more. Without a version, zustand would rehydrate that state and the UI
      // would compare a stale number against UUID strings, silently failing to
      // recognise the signed-in user as themselves. Returning undefined drops
      // the persisted state, so the app falls back to a clean signed-out start.
      version: 2,
      migrate: () => undefined,
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          state.user = withTokenOrgIds(state.user, state.accessToken);
        }
      },
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        impersonation: state.impersonation,
        mspStatus: state.mspStatus,
        companyStatus: state.companyStatus,
      }),
    },
  ),
);
